/**
 * Lightweight UTM-source capture for the Stella Polaris landing page.
 *
 * On every visit, parses utm_* params from the URL, persists them to
 * localStorage with a 30-day TTL, and exposes a getter that the application
 * form (and the admin dashboard later) can read.
 *
 * First-touch attribution: only writes to storage if there's nothing there yet
 * OR if the new visit carries fresh utm params (overrides). This means a
 * visitor who arrives via an ad and converts a week later still has the
 * original utm tagged on their submission.
 */

const STORAGE_KEY = "stella_polaris_utm_v1";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

const safeRead = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    if (obj.expires && obj.expires < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return obj;
  } catch {
    return null;
  }
};

const safeWrite = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore quota / privacy mode */
  }
};

/** Parse current URL for utm_* params + a referrer fallback. */
export const parseAttributionFromUrl = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  const out = {};
  let any = false;
  UTM_KEYS.forEach((k) => {
    const v = (params.get(k) || "").trim();
    if (v) {
      out[k] = v.slice(0, 200);
      any = true;
    }
  });
  return any ? out : null;
};

/**
 * Capture UTMs from the current URL into localStorage. Call once on app mount.
 * Returns the captured object (or the previously stored one).
 */
export const captureAttribution = () => {
  const fromUrl = parseAttributionFromUrl();
  const referrer =
    typeof document !== "undefined" && document.referrer
      ? document.referrer.slice(0, 300)
      : "";

  if (fromUrl) {
    const data = {
      ...fromUrl,
      referrer,
      landing_path: window.location.pathname,
      captured_at: new Date().toISOString(),
      expires: Date.now() + TTL_MS,
    };
    safeWrite(data);
    return data;
  }

  // No new utms — keep whatever's already stored (first-touch).
  const existing = safeRead();
  if (existing) return existing;

  // Nothing stored, no utm params — still record referrer if it exists,
  // so the admin can see "came from linkedin.com" even without a tagged link.
  if (referrer) {
    const data = {
      referrer,
      landing_path: window.location.pathname,
      captured_at: new Date().toISOString(),
      expires: Date.now() + TTL_MS,
    };
    safeWrite(data);
    return data;
  }
  return null;
};

/** Returns the stored attribution object (or null). Read-only. */
export const getAttribution = () => safeRead();

/**
 * Build the subset of attribution fields safe to attach to an application
 * payload. Strips internal bookkeeping (expires/captured_at).
 */
export const getAttributionForSubmission = () => {
  const a = safeRead();
  if (!a) return {};
  const out = {};
  UTM_KEYS.forEach((k) => {
    if (a[k]) out[k] = a[k];
  });
  if (a.referrer) out.referrer = a.referrer;
  if (a.landing_path) out.landing_path = a.landing_path;
  return out;
};
